import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { createTrackingUtils } from "../index";
import { getFirstDepartureDate, getLastArrivalDate, getSearchType } from "../../../store/searchBoxState/selectors";
import useRouteName from "../../../hooks/useRouteName";
import useUserAgent from "../../../hooks/useUserAgent";
import { ExperimentConfigWithDefault } from "@flights/et-universal";

const EXPERIMENT_CONFIG: Omit<ExperimentConfigWithDefault<string, string, string>, "name"> = {
  defaultStage: 1,
  stages: {
    main: 1,
    one_way: 2,
    round_trip: 3,
    multi_city: 4
  },
  goals: {
    click_on_popular_card: 1,
    change_date_on_sr: 2,
    change_date_on_index: 3,
    back_to_index_from_sr: 4
  },
  goalsWithValue: []
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const flights_web_cat_popular_flight_sb_dates_mdot = createTrackingUtils({
  name: "flights_web_cat_popular_flight_sb_dates_mdot",
  ...EXPERIMENT_CONFIG
});

// eslint-disable-next-line @typescript-eslint/naming-convention
const flights_web_cat_popular_flight_sb_dates_www = createTrackingUtils({
  name: "flights_web_cat_popular_flight_sb_dates_www",
  ...EXPERIMENT_CONFIG
});

const getExperimentUtils = (isMobile: boolean) => {
  return isMobile ? flights_web_cat_popular_flight_sb_dates_mdot : flights_web_cat_popular_flight_sb_dates_www;
};

const usePreviousDate = (date: string | undefined): string | undefined => {
  const dateRef = useRef<string | undefined>();
  useEffect(() => {
    dateRef.current = date;
  });
  return dateRef.current;
};

const trackExperimentStages = (
  utils: ReturnType<typeof createTrackingUtils>,
  searchType?: "ONEWAY" | "ROUNDTRIP" | "MULTISTOP"
) => {
  utils.stages.main();

  switch (searchType) {
    case "ONEWAY":
      utils.stages.one_way();
      break;
    case "ROUNDTRIP":
      utils.stages.round_trip();
      break;
    case "MULTISTOP":
      utils.stages.multi_city();
      break;
  }
};

export const usePopularFlightsDateTracking = () => {
  const { isMobile } = useUserAgent();
  const searchType = useSelector(getSearchType);
  const firstDepartureDate = useSelector(getFirstDepartureDate);
  const lastArrivalDate = useSelector(getLastArrivalDate);
  const [datesChanged, setDatesChanged] = useState<boolean>(false);
  const [datesInitialized, setDatesInitialized] = useState<boolean>(false);
  const prevFirstDepartureDate = usePreviousDate(firstDepartureDate);
  const prevLastArrivalDate = usePreviousDate(lastArrivalDate);
  const routeName = useRouteName();

  const utils = getExperimentUtils(isMobile);

  const trackStages = useCallback(
    (searchType?: "ONEWAY" | "ROUNDTRIP" | "MULTISTOP") => {
      trackExperimentStages(utils, searchType);
    },
    [utils]
  );

  const trackPopularCardClick = useCallback(() => {
    utils.goals.click_on_popular_card();
  }, [utils]);

  const trackBackToIndexFromSR = useCallback(() => {
    utils.goals.back_to_index_from_sr();
  }, [utils]);

  useEffect(() => {
    trackStages(searchType);
  }, [isMobile, searchType, trackStages]);

  // Track date changes
  useEffect(() => {
    if (!utils.trackWithDefaultStage()) return;
    if (!firstDepartureDate && !lastArrivalDate) return;

    if (!datesInitialized) {
      setDatesInitialized(true);
      return;
    }

    const departureChanged = prevFirstDepartureDate !== firstDepartureDate;
    const arrivalChanged = prevLastArrivalDate !== lastArrivalDate;
    const hasDateChanged = departureChanged || arrivalChanged;

    if (hasDateChanged) {
      setDatesChanged(true);
      routeName === "searchresults" ? utils.goals.change_date_on_sr() : utils.goals.change_date_on_index();
    }
  }, [
    firstDepartureDate,
    lastArrivalDate,
    prevFirstDepartureDate,
    prevLastArrivalDate,
    datesInitialized,
    routeName,
    isMobile,
    utils
  ]);

  return {
    datesChanged,
    searchType,
    firstDepartureDate,
    lastArrivalDate,
    trackStages,
    trackPopularCardClick,
    trackBackToIndexFromSR,
    isMobile
  };
};

export { flights_web_cat_popular_flight_sb_dates_www, flights_web_cat_popular_flight_sb_dates_mdot };
